import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Icono } from '@xiltrion/ui/icono';
import { Spinner } from '@xiltrion/ui/spinner';
import { Button } from '@xiltrion/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { Badge } from '@xiltrion/ui/badge';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { ToggleGroup, ToggleGroupItem } from '@xiltrion/ui/toggle-group';
import type { Task } from '@/api/types';
import { DueBadge, EmptyState, ErrorState, LoadingState, PageHeader, PriorityTag, STATUS_LABEL, StatusTag, fmtDate } from '@/components/ui';
import { useTableColumns } from '@/features/profile/api';
import { dueGroup } from '@/features/cases/lib';
import type { SupportRequestRow } from '@pqrs/contracts';
import { supportKeys } from '@/features/support/api';
import { SupportRequestsView } from '@/features/support/SupportRequestsView';
import { usePendingSupport } from '@/features/support/SupportTaskRow';
import { taskKeys, useTasks } from './api';

const GROUPS = [{ value: 'all', label: 'Todas' }, { value: 'overdue', label: 'Vencidas' }, { value: 'today', label: 'Vencen hoy' }, { value: 'tomorrow', label: 'Mañana' }, { value: 'blocked', label: 'Bloqueadas' }, { value: 'unassigned', label: 'Sin responsable' }];

/** Pestañas de «Mis tareas» (`?vista=`): las tareas (con los apoyos por responder) y los apoyos que pidió la persona. */
type View = 'tareas' | 'apoyos-enviados';

/** Una fila de la lista: una tarea del flujo o un apoyo que le piden a las áreas de la persona. */
type Row = { kind: 'task'; task: Task } | { kind: 'support'; support: SupportRequestRow };
const rowDue = (r: Row) => (r.kind === 'task' ? r.task.dueAt : r.support.case.dueAt);
const rowKey = (r: Row) => (r.kind === 'task' ? `task:${r.task.id}` : `support:${r.support.id}`);
const rowRadication = (r: Row) => (r.kind === 'task' ? r.task.radication : r.support.case.radication) ?? '';
const rowCaseType = (r: Row) => (r.kind === 'task' ? r.task.caseTypeName : r.support.case.caseTypeName) ?? '';
const rowName = (r: Row) => (r.kind === 'task' ? r.task.name : `Responder apoyo · ${r.support.targetUnit.name}`);
const rowSubject = (r: Row) => (r.kind === 'task' ? r.task.subject : r.support.case.subject) ?? '';

/**
 * Las mismas columnas y el mismo aire que la bandeja de solicitudes (`InboxPage`): radicado con el tipo debajo, el
 * vencimiento de segundo, el asunto con la especialidad, prioridad y estado. El grupo por vencimiento es una columna
 * oculta por la que agrupa la tabla; su valor lleva el orden delante para que los grupos salgan de lo urgente a lo lejano.
 */
const COLUMNAS: ColumnaTabla<Row>[] = [
  { key: 'grupo', titulo: 'Vencimiento', valor: (r) => { const g = dueGroup(rowDue(r)); return `${g.order} ${g.label}`; } },
  { key: 'radication', titulo: 'Radicado', ancho: '9.5rem', unaLinea: true, valor: rowRadication, secundario: (r) => rowCaseType(r) || null,
    render: (r) => <span className="tnum">{rowRadication(r) || <span className="text-muted-foreground">Pendiente</span>}</span> },
  { key: 'due', titulo: 'Vence', ancho: '10rem', unaLinea: true, valor: rowDue,
    render: (r) => r.kind === 'task' ? <DueBadge due={r.task.due} dueAt={r.task.dueAt} compacto /> : <span className="text-muted-foreground tnum">{r.support.case.dueAt ? `Caso vence ${fmtDate(r.support.case.dueAt, false)}` : '—'}</span> },
  { key: 'name', titulo: 'Tarea', lineas: 2, valor: rowName, secundario: (r) => (r.kind === 'support' ? `De ${r.support.requestedBy.name}` : null) },
  { key: 'subject', titulo: 'Tema / Asunto', lineas: 2, valor: rowSubject, secundario: (r) => (r.kind === 'task' ? r.task.specialtyName ?? null : null) },
  { key: 'priority', titulo: 'Prioridad', unaLinea: true, valor: (r) => (r.kind === 'task' ? r.task.priority ?? '' : ''),
    render: (r) => r.kind === 'task' && r.task.priority ? <PriorityTag priority={r.task.priority} /> : <span className="text-muted-foreground">—</span> },
  { key: 'status', titulo: 'Estado', unaLinea: true, valor: (r) => (r.kind === 'task' ? STATUS_LABEL[r.task.status] ?? r.task.status : 'Apoyo'),
    render: (r) => r.kind === 'task' ? <StatusTag status={r.task.status} /> : <Badge variant="warning-light" size="sm">Apoyo</Badge> },
];
const COLUMNAS_POR_DEFECTO = ['radication', 'due', 'name', 'subject', 'priority', 'status'];

/**
 * Mis tareas: las tareas agrupadas por vencimiento, junto con los apoyos que le piden a las áreas de la persona (se
 * responden como una tarea más, por el vencimiento del caso), y en otra pestaña los apoyos que ella pidió.
 */
export function TasksPage() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const view: View = sp.get('vista') === 'apoyos-enviados' ? 'apoyos-enviados' : 'tareas';
  const tasksFetching = useIsFetching({ queryKey: taskKeys.all }) > 0;
  const supportFetching = useIsFetching({ queryKey: supportKeys.all }) > 0;
  const fetching = view === 'tareas' ? tasksFetching || supportFetching : supportFetching;
  const refresh = () => { if (view === 'tareas') void qc.invalidateQueries({ queryKey: taskKeys.all }); void qc.invalidateQueries({ queryKey: supportKeys.all }); };
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Mis tareas" subtitle={view === 'tareas' ? 'Lo que tienes por hacer en tus casos, también los apoyos que le piden a tus áreas.' : 'Los apoyos que pediste a otras áreas para responder una PQRS. No se envían al ciudadano.'}
        actions={<Button variant="ghost" size="icon" aria-label="Actualizar" onClick={refresh} disabled={fetching}>{fetching ? <Spinner /> : <Icono nombre='mdi:refresh' />}</Button>} />
      {/* Cambiar de pestaña limpia los filtros de la otra (grupo de tareas; estado y texto de los apoyos). */}
      <Tabs value={view} onValueChange={(v) => setSp(v === 'tareas' ? {} : { vista: v }, { replace: true })}>
        <TabsList variant="line">
          <TabsTrigger value="tareas" className="gap-2"><Icono nombre="mdi:check-circle-outline" /> Tareas</TabsTrigger>
          <TabsTrigger value="apoyos-enviados" className="gap-2"><Icono nombre="mdi:send-outline" /> Apoyos enviados</TabsTrigger>
        </TabsList>
      </Tabs>
      {view === 'tareas' ? <TaskList /> : <SupportRequestsView box="sent" />}
    </div>
  );
}

/**
 * Las tareas de la persona (o las sin responsable) y los apoyos por responder de sus áreas, agrupados por vencimiento
 * (el apoyo, por el del caso). «Bloqueadas» y «Sin responsable» son solo de tareas.
 */
function TaskList() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const group = sp.get('grupo') ?? 'all';
  const [columns, setColumns] = useTableColumns('tasks', COLUMNAS_POR_DEFECTO);
  const mine = useTasks({ mine: true, limit: 200 });
  const unassigned = useTasks({ limit: 200 });
  const support = usePendingSupport();
  const q = group === 'unassigned' ? unassigned : mine;
  const rows = useMemo(() => {
    let list: Row[] = (q.data ?? []).map((task) => ({ kind: 'task', task }));
    if (group === 'unassigned') list = list.filter((r) => r.kind === 'task' && !r.task.assigneeUserId);
    else if (group === 'blocked') list = list.filter((r) => r.kind === 'task' && r.task.status === 'BLOCKED');
    else {
      list = [...list, ...(support.data ?? []).map((s): Row => ({ kind: 'support', support: s }))];
      if (group !== 'all') list = list.filter((r) => dueGroup(rowDue(r)).key === group);
    }
    return list;
  }, [q.data, support.data, group]);
  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup type="single" variant="outline" size="sm" value={group} onValueChange={(v) => { if (v) setSp(v === 'all' ? {} : { grupo: v }, { replace: true }); }} aria-label="Filtrar tareas" className="flex-wrap">
        {GROUPS.map((g) => <ToggleGroupItem key={g.value} value={g.value}>{g.label}</ToggleGroupItem>)}
      </ToggleGroup>
      {q.isLoading ? <LoadingState rows={6} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar tus tareas." /> : rows.length === 0 ? (
        <EmptyState title={group === 'all' ? 'No tienes tareas pendientes.' : 'No hay tareas en este grupo.'} hint={group === 'all' ? 'Cuando recibas una nueva tarea aparecerá aquí.' : 'Prueba con otro grupo o vuelve a “Todas”.'} icon="mdi:check-decagram-outline" action={group !== 'all' ? <Button variant="outline" size="sm" onClick={() => setSp({}, { replace: true })}>Ver todas</Button> : undefined} />
      ) : (
        <Tabla
          columnas={COLUMNAS} filas={rows} claveFila={rowKey} buscar={false} densa ordenInicial={{ key: 'due' }}
          onClicFila={(r) => nav(r.kind === 'task' ? `/tareas/${r.task.id}` : `/pqrs/${r.support.caseId}?tab=apoyos`)}
          columnasVisibles={columns} onColumnasVisiblesChange={setColumns} columnasPorDefecto={COLUMNAS_POR_DEFECTO}
          agrupar={{ por: 'grupo', titulo: (valor, filas) => {
            const order = Number(valor.slice(0, 1)); const label = valor.slice(2);
            return <><span className="inline-flex items-center gap-1.5">{order <= 1 ? <Icono nombre="mdi:alert-circle" className="text-destructive" aria-hidden /> : order === 2 ? <Icono nombre="mdi:alert" className="text-warning" aria-hidden /> : null}{label}</span><span className="tnum font-normal text-muted-foreground">({filas.length})</span></>;
          } }}
        />
      )}
    </div>
  );
}
